import React, { useState } from 'react';
import CardMCFilter from './CardMCFilter';
import { Button, Icon } from 'semantic-ui-react';
import * as XLSX from 'xlsx';
import CardMCVegaChart from './components/CardMCVegaChart';
import { VisualizationSpec } from 'vega-embed';

type CardMCRepeatInSixMonthProps = {
  // Add props if needed
  controller: any;
  runSequence: any;
  MeChartSequence: any;
}
// Component: CardMeChartRepeatInSixMonth
// Purpose: Displays a bar chart showing how frequently patients return within a 6-month period.
const CardMCRepeatInSixMonth = (props: CardMCRepeatInSixMonthProps) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(1);
  const [endYear, setEndYear] = useState(new Date().getFullYear());
  const [endMonth, setEndMonth] = useState(12);

  // State for chart data
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isTransposed, setIsTransposed] = useState(false);

  const onSearch = () => { fetchData(); };
  const onPrint = () => { window.print(); };

  const onDownload = () => {
    if (!chartData || chartData.length === 0) {
      alert('No data to download');
      return;
    }

    let dataToExport = chartData.map(item => ({
      "Frequency (Times)": item.frequency_category,
      "Patient Count": item.patient_count
    }));

    if (isTransposed) {
      // Transpose logic
      const headers = Object.keys(dataToExport[0]);
      const transposedData = [
        headers,
        ...dataToExport.map(row => headers.map((header: string) => (row as any)[header]))
      ];
      const pivotedData = transposedData[0].map((_, colIndex) => transposedData.map(row => row[colIndex]));
      const ws = XLSX.utils.aoa_to_sheet(pivotedData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "RepeatCount");
      XLSX.writeFile(wb, `Repeat_Patient_Frequency_Transposed_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } else {
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "RepeatCount");
      XLSX.writeFile(wb, `Repeat_Patient_Frequency_${new Date().toISOString().slice(0, 10)}.xlsx`);
    }
  };

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);
    try {
      const startDateStr = `${year}-${String(month).padStart(2, '0')}-01 00:00:00`;
      const endDateStr = startDateStr;
      const sixBefore = new Date(year, month - 1 - 6, 1);
      const behide6Month = `${sixBefore.getFullYear()}-${String(sixBefore.getMonth() + 1).padStart(2, '0')}-01 00:00:00`;
      // const startYearStr = `${year}-01-01 00:00:00`;
      // const endYearStr = `${year}-12-31 23:59:59`;

      let res = null
      let data = null
      if (props.MeChartSequence?.sequenceIndex === "Action") {
        res = await props.runSequence({
          sequence: "MeChart", action: "QuerySQL",
          chart: "frequency-visit-within-six-month",
          startDate: behide6Month,
          endDate: endDateStr
        })

        console.log('res: ', res);
        if (res?.length > 0 && res?.[0]?.items && Array.isArray(res?.[0]?.items)) {
          data = res?.[0]?.items;
        }
      }

      if (Array.isArray(data)) {
        setChartData(data);
      } else {
        setChartData([]);
      }
      setLoading(false);

      const response = await fetch(`http://localhost:3002/api/repeat-patient-frequency?startDate=${behide6Month}&endDate=${endDateStr}`);
      const sqlData = await response.json();
      console.log('sqlData: ', sqlData);

    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  // Initial fetch
  React.useEffect(() => {
    fetchData();
  }, []);

  // Compute displayed date range: end = last day of selected month, start = 6 months earlier
  const getDateRangeLabel = () => {
    const endDate = new Date(year, month, 0); // last day of selected month
    const startDate = new Date(year, month - 7, 1); // 6 months back, first day
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return `${fmt(startDate)} to ${fmt(endDate)}`;
  };

  // --- Vega Data Transformation ---

  const getVegaData = () => {
    // Expected categories: '1', '2', '3', '4', '5', '>5'
    const categories = ['1', '2', '3', '4', '5', '>5'];
    const labels = ['1 ครั้ง', '2 ครั้ง', '3 ครั้ง', '4 ครั้ง', '5 ครั้ง', 'มากกว่า 5 ครั้ง'];
    const dataMap = new Map();
    chartData.forEach(item => {
      dataMap.set(item.frequency_category, parseInt(item.patient_count || 0));
    });

    return categories.map((cat, index) => ({
      category: labels[index],
      value: dataMap.get(cat) || 0,
      color: '#34A853'
    }));
  };

  // --- Vega Specs ---

  const getSpec = (): VisualizationSpec => {
    const xField = isTransposed ? 'value' : 'category';
    const yField = isTransposed ? 'category' : 'value';

    return {
      width: "container",
      height: "container",
      autosize: { type: "fit", contains: "padding" },
      data: { values: getVegaData() },
      mark: { type: "bar", tooltip: true },
      encoding: {
        [isTransposed ? 'y' : 'x']: {
          field: "category",
          type: "ordinal",
          axis: { title: "Frequency", labelAngle: isTransposed ? 0 : 0 },
          sort: null // Maintain custom order
        },
        [isTransposed ? 'x' : 'y']: {
          field: "value",
          type: "quantitative",
          axis: { title: "Patient Count" }
        },
        color: { value: "#34A853" },
        tooltip: [
          { field: "category", title: "Frequency" },
          { field: "value", title: "Count", format: "," }
        ]
      }
    };
  };

  return (
    <div id="card-mc-repeat-in-six-month">
      {/* Row 1: Toggles */}
      <div className="controls-container">
        {/* Data View Toggles */}
        <Button.Group>
          <Button
            active={true}
            style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }}
          >
            จำนวนผู้รับบริการกลับมาใช้บริการซํ้าภายใน 6 เดือน
          </Button>
        </Button.Group>

        {/* Chart Type Toggles */}
        <Button.Group>
          <Button
            active={true}
            style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white', cursor: 'default' }}
            icon
          >
            <Icon name='chart bar' /> Bar
          </Button>
        </Button.Group>
      </div>

      {/* Row 2: Filter and Download */}
      <div className="filter-container">
        <CardMCFilter
          mode='oneYearOneMonth'
          year={year}
          month={month}
          endYear={endYear}
          endMonth={endMonth}
          onYearChange={setYear}
          onMonthChange={setMonth}
          onEndYearChange={setEndYear}
          onEndMonthChange={setEndMonth}
          onSearch={onSearch}
          onPrint={onPrint}
        >
          <Button style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }} size='small' icon labelPosition='left' onClick={onDownload}>
            <Icon name='download' />
            Download .xlsx
          </Button>
          <Button style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }} size='small' icon labelPosition='left' onClick={() => setIsTransposed(!isTransposed)} title="Transpose">
            <Icon name='exchange' />
            Transpose
          </Button>
        </CardMCFilter>
        <div style={{ color: '#888', fontSize: '0.85em', marginTop: '4px', paddingLeft: '4px' }}>
          ช่วงข้อมูล: {getDateRangeLabel()}
        </div>
      </div>

      {/* Row 4: Chart */}
      <div className="chart-wrapper">
        {loading ? (
          <div>Loading...</div>
        ) : (
          <CardMCVegaChart spec={getSpec()} actions={false} />
        )}
      </div>
    </div>
  );
};

export default CardMCRepeatInSixMonth;
